import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Option } from '@mylighthouse/prism-ember/types/components/prism/select-input';
import {
  type Breakdown,
  type CentralMappingMappingEntryExtendedModel,
  FILTER_OPTIONS,
  FILTER_OPTIONS_TRANSLATIONS,
  type FilterOption,
} from 'lumen/utils/central-mapping-constants';

export type DropdownFilterOption = Option & { id: FilterOption };

interface SettingsCentralMappingDropdownsDropdownFilterSignature {
  Args: {
    breakdown: Breakdown;
    filterOption: FilterOption;
    mapping: CentralMappingMappingEntryExtendedModel[];
    setFilterOption: (filter: FilterOption) => void;
  };
}

export default class SettingsCentralMappingDropdownsDropdownFilter extends Component<SettingsCentralMappingDropdownsDropdownFilterSignature> {
  @service declare intl: Services['intl'];

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): DropdownFilterOption[] {
    const { mapping } = this.args;

    return [
      {
        label: this.intl.t(FILTER_OPTIONS_TRANSLATIONS[FILTER_OPTIONS.ALL]),
        id: FILTER_OPTIONS.ALL,
      },
      {
        label: this.intl.t(FILTER_OPTIONS_TRANSLATIONS[FILTER_OPTIONS.UNSAVED]),
        id: FILTER_OPTIONS.UNSAVED,
        badge: mapping
          .filter((mappingEntry) => mappingEntry.isUnsaved)
          .length.toString(),
        badgeType: 'neutral',
      },
    ];
  }

  get value(): DropdownFilterOption | undefined {
    return this.options.find((option) => option.id === this.args.filterOption);
  }

  @action
  onChange(option: Option): void {
    const typedOption = option as DropdownFilterOption;

    this.args.setFilterOption(typedOption.id);
  }

  <template>
    <PrismSelectInput
      @onChange={{this.onChange}}
      @options={{this.options}}
      @value={{this.value}}
      data-test-id="dropdown-filter"
    />
  </template>
}
